import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  useMediaQuery,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { FC, useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import NumberFormat from "react-number-format";
import { TopupPayload } from "../../../../../types";
import { getValue, needMultiply } from "../../../../../utils/inputTransform";
import { AxiosError } from "../../../../../utils/api/axiosErrorType";
import Joi from "joi";
import { BillingAPI } from "../../../../../utils/api/requests/billing-requests";
import { tagManagerEvent } from "../../../../../utils/tagManagerEvent";
import { theme } from "../../../../../theme";
import { useTranslation } from "react-i18next";

interface TopupModalProps {
  open: boolean;
  handleClose: () => void;
}

export const TopupModal: FC<TopupModalProps> = ({ open, handleClose }) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const form = useForm<TopupPayload>({
    mode: "onChange",
    defaultValues: { type: "PAYCRYPTO" }, // Установка значения по умолчанию для типа
    resolver: joiResolver(
      Joi.object({
        amount: Joi.number().required().min(1),
        type: Joi.string().required().valid('PAYCRYPTO', 'YOOKASSA'),
      })
    ),
  });
  const { handleSubmit, formState, control, reset } = form;

  const { isDirty, isValid } = formState;

  const { mutate, isLoading } = useMutation({
    mutationFn: BillingAPI.topup,
    onSuccess: (data) => {
      window.location.href = data.url;
      // if (isMobile) {
      //   window.location.href = data.url;
      // } else {
      //   window.open(data.url, "_blank");
      // }

      handleClose();
    },
    onError: (error) => {
      const err = error as AxiosError;
      toast.error(err?.response?.data?.detail);
    },
  });

  const onSubmit = async (formData: TopupPayload) => {
    tagManagerEvent("Topup", "topup_button");
    mutate(formData);
  };

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  if (!open) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth={isMobile}
    >
      <FormProvider {...form}>
        <Box component={"form"} onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle id="alert-dialog-title">
            {t("balance.topUp")}
          </DialogTitle>
          <DialogContent
            sx={{
              display: "flex",
              flexDirection: "column",
              width: { xs: "sm", sm: "350px", md: "350px" },
            }}
          >
            <Typography sx={{ mb: 2 }}>{t("balance.content")}</Typography>
            <Controller
              control={control}
              name="type"
              render={({ field }) => (
                <RadioGroup
                  {...field}
                  aria-labelledby="type-radio-buttons-group-label"
                  name="type-radio-buttons-group"
                  sx={{ mb: "20px" }} // Изменено на mb для отступа от следующего поля
                >
                  <FormControlLabel
                      value="PAYCRYPTO"
                      control={<Radio />}
                      label={
                        <>
                          <strong>PayСrypto.global</strong><br />
                          {t("balance.paycrypto")}
                        </>
                      }
                      sx={{ mb: 1 }}
                  />
                  <FormControlLabel
                      value="YOOKASSA"
                      control={<Radio />}
                      label={
                        <>
                          <strong>YooKassa</strong><br />
                          {t("balance.yookassa")}
                        </>
                      }
                      sx={{ mb: 1 }}
                  />
                </RadioGroup>
              )}
            />
            <Controller
              control={control}
              name="amount"
              render={({ field: { ref, onChange, value, ...field }, fieldState: { error } }) => (
                <NumberFormat
                  customInput={TextField}
                  prefix="$"
                  inputRef={ref}
                  onValueChange={needMultiply(1, onChange)}
                  value={getValue(value, true, 1)}
                  type="text"
                  variant="outlined"
                  label={t("account.amount")}
                  error={!!error}
                  fullWidth
                  decimalScale={0}
                  autoComplete="off"
                  allowNegative={false}
                  sx={{ mt: "12px" }} // Изменено для согласованности
                />
              )}
            />
          </DialogContent>
          <DialogActions sx={{ marginBottom: "10px", px: "24px" }}>
            <Button variant="outlined" onClick={handleClose}>
              {t("cancel")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={!isDirty || !isValid || isLoading}
            >
              {t("balance.topUp")}
            </Button>
          </DialogActions>
        </Box>
      </FormProvider>
    </Dialog>
  );
};
